/**
 * Frontend supply-term labels — mirrors api/services/supplyTermService.js
 * (the single source of truth; the Vite boundary prevents importing it
 * directly, so this file is hand-maintained and must be kept symmetric).
 *
 * STRICT KEY POLICY: the only valid storage duration keys are "1", "3", "6"
 * and "12". Any other key is a DATA DEFECT — it is never derived into a usable
 * term, never labelled as a term the catalog sells, and never offered as a
 * selectable supply option. It is reported once via a structured warning and
 * resolved to an explicitly unsupported term (`isSupported: false`).
 *
 * Storage duration keys are UNCHANGED — only the customer-facing label
 * changes: key "1" renders "60 Day". Prices carry over 1:1 (never multiply a
 * customer-facing price as part of the relabel).
 *
 * Prefer API-provided labels (product supplyPricingMeta[key].label, emitted
 * by the backend since commit caf45d7) and use these helpers as the fallback
 * for keys without meta (orders, legacy flat pricing).
 */

export const SUPPLY_TERMS = Object.freeze({
  '1': Object.freeze({ key: '1', label: '60 Day', internalMonths: 2 }),
  '3': Object.freeze({ key: '3', label: '3 Months', internalMonths: 3 }),
  '6': Object.freeze({ key: '6', label: '6 Months', internalMonths: 6 }),
  '12': Object.freeze({ key: '12', label: '12 Months', internalMonths: 12 })
});

// Safe finite shape for a key that is NOT in SUPPLY_TERMS. The key is carried
// through verbatim so a defect is never silently remapped onto a real term.
// The label must not be shaped like a catalog term and must not interpolate the
// caller-supplied key -- see the api twin for both reasons (card 53639). The
// parity suite binds this constant to its api counterpart; change them together.
const UNSUPPORTED_LABEL = 'Unsupported';
const UNSUPPORTED_INTERNAL_MONTHS = 1;

const normalizeKey = (durationKey) => String(durationKey ?? '').trim();

/**
 * True only for the four supported storage duration keys, as string or numeric
 * (padded values are trimmed first). Mirrors isSupportedDurationKey in
 * api/services/supplyTermService.js.
 */
export const isSupportedDurationKey = (durationKey) =>
  Object.prototype.hasOwnProperty.call(SUPPLY_TERMS, normalizeKey(durationKey));

const SUPPORTED_TERMS = Object.freeze(
  Object.fromEntries(
    Object.entries(SUPPLY_TERMS).map(([key, term]) => [
      key,
      Object.freeze({ ...term, isSupported: true })
    ])
  )
);

// Report each defective key once, not once per render.
const warnedDurationKeys = new Set();

const reportUnsupportedDurationKey = (key) => {
  if (warnedDurationKeys.has(key)) return;
  warnedDurationKeys.add(key);
  console.warn('[supplyTerm] unsupported duration key', { key });
};

/**
 * Resolve a duration key (string or numeric) to its supply term.
 * Supported keys return their configured term with isSupported true; every
 * other key is reported as a data defect and returns an explicitly unsupported
 * term with a safe finite internalMonths. Never throws.
 */
export const getSupplyTerm = (durationKey) => {
  const key = normalizeKey(durationKey);
  if (isSupportedDurationKey(key)) return SUPPORTED_TERMS[key];

  reportUnsupportedDurationKey(key);
  return Object.freeze({
    key,
    label: UNSUPPORTED_LABEL,
    internalMonths: UNSUPPORTED_INTERNAL_MONTHS,
    isSupported: false
  });
};

export const getSupplyLabel = (durationKey) => getSupplyTerm(durationKey).label;

/**
 * A stored supply_pricing rate is priced per RAW KEY unit, because pricingEngine charges
 * `stored x Number(key)`. But the "1" key sells a 60 Day term - TWO internal months of
 * medication - so the honest per-month rate a human reads is
 * `stored x Number(key) / internalMonths(key)`: a no-op for "3"/"6"/"12", a halving for "1".
 *
 * DISPLAY ONLY. Every total stays `stored x Number(key)`, which is what the gateway charges.
 * Mirrors toPerMonthRate in api/services/startingAtService.js - the two must agree, or the
 * catalog's "Starting at $X/mo" contradicts the product page on the same dosage.
 *
 * Pass the duration key the money is actually STORED under, not the one the member selected:
 * a caller that falls back to another key must divide by THAT key's internal months.
 */
export const toPerMonthRate = (cents, durationKey) => {
  const units = Number(durationKey);
  if (!Number.isFinite(units) || units <= 0) return cents;
  return Math.round((cents * units) / getSupplyTerm(durationKey).internalMonths);
};

/**
 * Starting-at supply basis -> storage duration key. Mirrors BASIS_DURATION_KEYS in
 * api/services/startingAtService.js. `all` maps to null on purpose: it spans every
 * duration, so the product advertises no single term to pre-select.
 */
const BASIS_DURATION_KEYS = Object.freeze({
  '60_day': '1',
  '3_month': '3',
  '6_month': '6',
  '12_month': '12'
});

const getBasisDurationKey = (basis) => BASIS_DURATION_KEYS[basis] ?? null;

/**
 * Duration keys a dosage can actually be bought at: a supported key, present in
 * supplyPricing and not disabled in meta. The two key sets can differ (a duration
 * can be described in meta with no rate in supplyPricing, and vice versa) —
 * supplyPricing is the one that can be quoted, so it is the one that decides.
 * An unsupported key is a data defect and is dropped here, so it can never be
 * offered as a selectable term.
 */
export const getAvailableSupplyKeys = (dosage) => {
  const pricing = dosage?.supplyPricing;
  if (!pricing || typeof pricing !== 'object') return [];
  const meta = dosage?.supplyPricingMeta || {};
  return Object.keys(pricing).filter(
    (key) => isSupportedDurationKey(key) && meta[key]?.enabled !== false
  );
};

/**
 * The supply term a product advertises — its starting-at basis — as a number of
 * months, but only when the given dosage can actually be sold at that term.
 *
 * Returns null (caller keeps its own default) when the basis is `all`, absent
 * (STARTING_AT_BASIS_V2=false makes the API omit `startingAt` entirely), or names
 * a duration this dosage does not offer. Never throws, never invents a term.
 *
 * This is what makes the catalog's "Starting at $X/mo" and the term checkout opens
 * on refer to the same thing.
 */
export const resolveBasisSupplyMonths = (product, dosage) => {
  const key = getBasisDurationKey(product?.startingAt?.basis);
  if (!key || !getAvailableSupplyKeys(dosage).includes(key)) return null;
  const months = Number(key);
  return Number.isInteger(months) && months > 0 ? months : null;
};

/**
 * Cheapest rate a customer can actually be QUOTED for `tier`, across a product's
 * dosages — the number behind the catalog's "Starting at $X/mo" when the API
 * omits `startingAtPrice` (STARTING_AT_BASIS_V2=false).
 *
 * Duration keys come from getAvailableSupplyKeys, so a term disabled in meta, or
 * carrying an unsupported key, can never set the headline. That is the whole
 * point: the catalog must not advertise a price no selectable term can produce.
 * web/pages/admin/ProductsPage.jsx applies the same `enabled` rule when staff set
 * Starting At — this keeps the customer-facing number on that same rule instead
 * of a third hand-maintained copy of the predicate.
 *
 * Reads the tier-keyed shape `{ "3": { access, one, plus } }`, falling back to
 * the `access` rate the way pricingEngine does. A dosage with no supplyPricing at
 * all falls back to its flat `price`. Returns null when nothing is priceable.
 */
export const computeTierStartingAtCents = (dosages, tier = 'access') => {
  if (!Array.isArray(dosages) || dosages.length === 0) return null;
  const lowest = dosages.reduce((acc, dosage) => {
    if (dosage?.isActive === false) return acc;
    const pricing = dosage?.supplyPricing;
    if (!pricing || typeof pricing !== 'object') {
      return dosage?.price > 0 && dosage.price < acc ? dosage.price : acc;
    }
    return getAvailableSupplyKeys(dosage).reduce((inner, key) => {
      const rates = pricing[key];
      if (!rates || typeof rates !== 'object') return inner;
      const cents = rates[tier] ?? rates.access ?? 0;
      return cents > 0 && cents < inner ? cents : inner;
    }, acc);
  }, Infinity);
  return lowest === Infinity ? null : lowest;
};
